<script lang="ts" setup>
import { requirejs } from '../modules/require';
import { useRoute, useRouter } from 'vue-router';
import { onMounted, ref } from 'vue';
import { useTranslations } from '~/composables/translations';
import { useMeStore } from '~/stores/me';
import { useCartStore } from '~/stores/cart';
import $fetch from '../modules/fetch';

import ProfileModal from '~/components/topbar/ProfileModal.vue';
import { getRegistryUrl } from '~/modules/compass-services';

const me = useMeStore();
const cart = useCartStore();
const router = useRouter();
const route = useRoute();
const { $i18n } = useTranslations('topbar');
const loading = ref(true);
const showProfileModal = ref(false);
const passportURL = ref('');

// OPEN LANGUAGE MODAL
const openLanguageModal = async () => {
  const [LanguagePicker, UWPI18n, MP3DPData] = await requirejs<[any, any, any]>([
    'DS/W3DXComponents/LanguagePicker',
    'DS/UWPClientCode/I18n',
    'DS/MP3DPApplicationStore/MP3DPData',
  ]);
  const languagePicker = new LanguagePicker({
    availableLanguagesGetter() {
      const languages = UWPI18n.getSupportedLanguages();
      // Remove non-traducted languages
      delete languages.cs;
      delete languages.ko;
      delete languages.pl;
      delete languages.ru;
      delete languages['pt-BR'];
      delete languages['zh-TW'];
      return languages;
    },
    currentLanguageGetter: UWPI18n.getCurrentLanguage,
    currentLanguageSetter: UWPI18n.setCurrentLanguage,
    passportUrl: await MP3DPData._getServiceUrl('3DPassport'),
  });
  languagePicker.render().inject(document.body);
  languagePicker.show();
};

// Confidentiality Modal
const openConfidentialityDialog = async () => {
  const [MP3DPConfidentialityModal, SuperModal, Vue] = await requirejs<[any, any, any]>([
    'DS/MP3DPWAPConfig/MP3DPConfidentialityModal',
    'DS/UIKIT/SuperModal',
    'vuejs',
  ]);
  const modal = new SuperModal();
  const ConfidentialityModalContructor = Vue.extend(MP3DPConfidentialityModal);
  const confidentialityModalInstance = new ConfidentialityModalContructor({
    propsData: { sharedParts: me.isSharedPartsAccepted },
  });

  const modalButtons = [
    {
      className: 'primary',
      value: $i18n('save'),
      action(m: any) {
        // TODO SAVE VALUE SELECTED IN MODAL
        m.hide();
        const me = useMeStore();
        me.setSharedParts(confidentialityModalInstance.$data.sharedPartsModel);
        confidentialityModalInstance.$destroy();
      },
    },
    {
      className: 'default',
      value: $i18n('cancel'),
      action: (m: any) => m.hide(),
    },
  ];
  confidentialityModalInstance.$mount();
  modal.dialog({
    body: confidentialityModalInstance.$el,
    closable: true,
    title: $i18n('confidentialityPreferences'),
    buttons: modalButtons,
  });
};

const returnToHomePage = () => {
  router.push({ name: 'Welcome' });
};

// If we have something in the cart, we save it in Storage to Use CartRecuperator on re-comming to the Page
const redirectUserToLogin = (passportUrl: string) => {
  if (cart.testPartMode || (!cart.cart.id && cart.printItems.length)) {
    cart.saveCartInLocalStorage();
  }

  window.location.assign(
    `${passportUrl}/login?service=${encodeURIComponent(window.location.href)}`,
  );
};

const injectLinkInTopBar = () => {
  const DSLogoOfTopbar = document.querySelector('#topbar .topbar-logo-large') as HTMLElement;

  if (DSLogoOfTopbar) {
    DSLogoOfTopbar.style.cursor = 'pointer';
    DSLogoOfTopbar.addEventListener('click', () => returnToHomePage());
  }
};

const setTopBarMultiShopMode = async () => {
  const [TopBarMultiShopsComponent, TopBar] = await requirejs<[any, any, any]>([
    'DS/MPFTopBarMultiShopsComponent/TopBarMultiShopsComponent',
    'DS/TopBar/TopBar',
  ]);
  const topBarContext = document.querySelector(
    '.topbar-user-context-wrapper .topbar-user-context-text',
  ) as any;
  const topbarContainer = document.querySelector('#topbar');
  topbarContainer?.classList.add('multi-shop-mode');

  const topbarmultishop = new TopBarMultiShopsComponent({
    selectedLabId: me.currentShop,
    onInit(item: any) {
      if (item.shopsCount > 1) {
        TopBar.view._updateContextTitle(item.text);
        topBarContext.setText(item.text);
        topbarmultishop.setTarget(topBarContext);
        topbarmultishop.render();
        _customClasses(item);
      }
    },
    onSelection(item: any) {
      topBarContext.setText(item.text);
      me.setCurrentShop(item.id);
      router.push({ name: route.name as string, params: { id: item.id } });
    },
    targetContainer: topBarContext,
  });
};

const remplaceAppTitleWithLink = () => {
  const topbarTitle = document.querySelector('.topbar-app .topbar-app-brand') as HTMLElement;
  const topbarCurrentText = topbarTitle.innerText;

  const anchorElement = document.createElement('a');
  anchorElement.classList.add('mp-top-frame-link');
  anchorElement.href = '/welcome';
  anchorElement.addEventListener('click', (e) => {
    e.preventDefault();
    return returnToHomePage();
  });
  anchorElement.innerText = topbarCurrentText;

  topbarTitle.innerHTML = '';
  topbarTitle.appendChild(anchorElement);
};

/*
 * Show the platform TopBar "extra button" as the addin close button.
 */
const addCloseButtonInTopBar = (TopBar: any) => {
  try {
    if (typeof TopBar.showExtrabtnArea !== 'function') {
      console.warn('TopBar: showExtrabtnArea is unavailable, addin close button not added');
      return;
    }

    TopBar.setExtraButtonOptions({
      tooltip: $i18n('close'),
      fonticon: 'fonticon-close',
    });
    TopBar.showExtrabtnArea();

    const extraButton = document.querySelector<HTMLElement>('#topbar .topbar-cmd.extrabtn');
    if (extraButton) {
      extraButton.style.display = 'block';
    }

    const caretButton = document.querySelector<HTMLElement>('#topbar .topbar-caret-dropdown-btn');
    if (caretButton) {
      caretButton.style.display = 'none';
    }

    TopBar._model.listenTo(TopBar._model, 'click-extrabtn', () => {
      window._addin_publish('CLOSE'); /* Event to close Addin */
    });
  } catch (e) {
    console.warn('TopBar: unable to add the addin close button', e);
  }
};

const _customClasses = (item: any) => {
  const topBarElement = document.querySelector('.topbar-user') as HTMLElement;
  const topBarContextWrapper = document.querySelector(
    '.topbar-user-context-wrapper',
  ) as HTMLElement;
  const topbarTitle = document.querySelector('.topbar-app') as HTMLElement;
  topbarTitle.classList.add('flex');
  topbarTitle.classList.add('align-items-center');

  if (topBarElement && (item ? item.shopsCount === 1 : true)) {
    topBarElement.classList.remove('topbar-user-context');
    topBarContextWrapper.classList.add('hide');
  }
};

const makeTopBarLinkScrollable = () => {
  const elements = document.querySelectorAll('.topbar-access-link');
  elements.forEach((e) => {
    e.setAttribute('href', '#');
  });
};

// Setup WAP Registry URL to make Swym and others app to work with WAPEssential
// TO REMOVE in FD04
const setUpWAPConfiguration = async () => {
  const [PublicAPI] = await requirejs<[any]>(['DS/UWPClientCode/PublicAPI']);
  const registryURL = await getRegistryUrl();

  PublicAPI.applicationConfigurations.push({
    propertyKey: 'app.urls.registry',
    propertyValue: registryURL,
    description: 'Registry endpoint.',
  });
};

// eslint-disable-next-line max-statements, complexity
const buildTopBar = async () => {
  const topBarContainer = document.querySelector('#topbar');

  if (topBarContainer) {
    // The real topbar is already in the DOM (component remounted): keep it and
    // drop the placeholder, otherwise both are displayed.
    loading.value = false;
    return;
  }

  loading.value = true;

  await requirejs([]);

  // Rewrite code from WAP to enabled DATA to load
  window.define(
    'DS/UWPClientCode/UWA/Environment',
    ['DS/MP3DPApplicationStore/MP3DPEnvironnement'],
    (Env: any) => Env,
  );
  window.define('UWA/Environments/Swym', ['UWA/Core', 'UWA/Environment'], (UWA: any, Env: any) => {
    const Make = Env.extend({ name: 'make' });
    return UWA.namespace('Environments/Make', Make, UWA);
  });

  await me.fetchMe();

  if (!me.me) {
    window.define('DS/TagNavigator/TagNavigator', [], () => null);
  }

  const [TopFrame, TopBarProxy, TopBar, UWPi18n] = await requirejs<
    [any, any, any, any, any, any, any, any, any]
  >([
    'DS/TopFrame/TopFrame',
    'DS/TopBarProxy/TopBarProxy',
    'DS/TopBar/TopBar',
    'DS/UWPClientCode/I18n',
    'css!TopFrame/TopFrame',
    'css!TopBar/TopBar',
  ]);

  const user = me.me;
  const tenant: string = await $fetch.getTenant();
  const passportUrl: string = await $fetch.getServiceURL('3DPASSPORT');
  passportURL.value = passportUrl;

  const myAppsUrl: string = await $fetch.getServiceURL('3DCompass');
  const tagsServerUrl: string = await $fetch.getServiceURL('6WTAG');
  const swymUrl: string = await $fetch.getServiceURL('3DSwym');
  const messagingUrl: string = await $fetch.getServiceURL('3DMessaging');
  const taggerUrl: string = await $fetch.getServiceURL('6wtag');
  const makeUrl: string = await $fetch.getServiceURL('MPPresentation');
  const isMakeWebInWin = ['catiav5', 'solidworks', 'catia3DExperience'].includes(
    (route.query.source as string) ?? '',
  );

  if (user?.name) {
    await setUpWAPConfiguration();
  }

  TopFrame.init({
    serviceUrl: makeUrl,
    prefixUrl: '/uwp/',
    passportUrl,
    myAppsBaseURL: myAppsUrl,
    tagsServerUrl,
    activateSearch: false,
    serviceName: '3dPrint',
    appNameAbout: 'Make',
    baseImgPath: '/webapps/i3DXCompass/assets/images/',
    baseHtmlPath: '/webapps/i3DXCompass/',
    baseAppletPath: '/resources/Compass/applet/',
    baseNlsPath: '/webapps/i3DXCompass/assets/lang/',
    brand: 'Make',
    hideTenantDisplay: true,
    lockedAppMode: false, // TODO
    tenant,
    userId: user?.name || '',
    userName: user ? `${user?.firstName} ${user?.lastName}` : '',
    lang: UWPi18n.getCurrentLanguage() || 'en',
    showCoachmark: false,
    renderFooter: false,
    activateLegal: Boolean(user),
    useTagger: false,
    activateFederatedSearch: false,
    activateInstantMessaging: Boolean(user),
    loginAction() {
      return redirectUserToLogin(passportUrl);
    },
    signupUrl: `${passportUrl}/login?service=${encodeURIComponent(window.location.href)}#register`,
    activateGDPR: true,
    topMenus: {
      profile: true,
      content: !isMakeWebInWin,
      social: !isMakeWebInWin,
      notification: !isMakeWebInWin,
      help: true,
    },
    legalTenantId: tenant,
    ko: false,
    legal: [
      {
        accepted: true,
        acceptedDP: false,
        acceptedTOS: false,
        cookie: {
          accepted: false,
          file: '',
          message: '',
        },
        displayName: tenant,
        dp: '',
        footerDisplay: false,
        id: tenant,
        legalInfo: '',
        manager: false,
        message: '',
        tos: '',
      },
    ],
    community: 3,
    startupParams: {
      admin: false,
      swym: [
        {
          displayName: tenant,
          id: tenant,
          url: swymUrl,
        },
      ],
      code: 0,
      hpc: [],
      instantMessaging: [
        {
          displayName: tenant,
          enabled: true,
          id: tenant,
          url: messagingUrl,
        },
      ],
      tagger: [
        {
          displayName: tenant,
          id: tenant,
          url: taggerUrl,
        },
      ],
      pending: false,
      roles: [],
      showCoachmark: false,
      success: true,
    },
  });

  const topBarProxy = new TopBarProxy({ id: 'proxy' });
  const topBarProxyContent = {
    profile: [
      {
        label: $i18n('myProfileItem'),
        onExecute() {
          showProfileModal.value = true;
        },
      },
      {
        label: $i18n('languages'),
        onExecute: openLanguageModal,
      },
      {
        id: 'confidentiality-topbar-menu',
        label: $i18n('confidentiality'),
        onExecute: openConfidentialityDialog,
      },
      {
        label: $i18n('signOut'),
        id: 'logout-topbar-menu',
        async onExecute() {
          // Remove this param to show the cart recup modal agin if user has logout
          localStorage.removeItem('recup-modal-timeout');

          cart.resetCart();

          const passportUrl = await $fetch.getServiceURL('3DPassport');
          const url = new URL(window.location.href);
          url.searchParams.delete('esid');
          url.searchParams.delete('ticket');
          url.pathname = '/';

          try {
            await $fetch.get('/servlet/logout', { service: '3DCompass' });
          } catch (e) {
            console.log(e);
          }

          const redirection = encodeURIComponent(url.toString());
          window.location.assign(`${passportUrl}/cas/logout?service=${redirection}`);
        },
      },
    ],
    help: [
      {
        label: $i18n('faqLink'),
        onExecute() {
          router.push({ name: 'FAQ' });
        },
      },
      {
        label: $i18n('wikiProcessesLink'),
        onExecute() {
          router.push({ name: 'WikiProcesses' });
        },
      },
      {
        label: $i18n('wikiMaterialsLink'),
        onExecute() {
          router.push({ name: 'WikiMaterials' });
        },
      },
    ],
  };

  topBarProxy.setContent(topBarProxyContent);
  TopBar.MainMenuBar.setActiveMenuProviders('proxy');

  if (isMakeWebInWin && user) {
    addCloseButtonInTopBar(TopBar);
  }

  injectLinkInTopBar();
  makeTopBarLinkScrollable();
  loading.value = false;

  if (user) {
    if (me.shops.length > 1) {
      await setTopBarMultiShopMode();
    }
  }

  remplaceAppTitleWithLink();
};

onMounted(async () => {
  // In Some Cases, RequestIdleCallBack is Not working
  if (window.requestIdleCallback) {
    window.requestIdleCallback(() => {
      setTimeout(buildTopBar, 0);
    });
  } else {
    setTimeout(buildTopBar, 0);
  }

  const [MP3DPNotificationCenter] = await requirejs<[NotificationCenter]>([
    'DS/MP3DPServices/MP3DPNotificationCenter',
  ]);
  const notificationCenter = MP3DPNotificationCenter.getInterWidgetNotificationCenter();

  notificationCenter.subscribe('common.3DNotification.topic', (event: any) => {
    const url = event?.data?.url;
    if (event?.action === 'refresh' && url) {
      console.log('Redirecting to:', url);
      const segments = url.split('/');
      const orderId = segments.pop()?.trim();
      if (orderId) {
        router.push({ name: 'Bidding', params: { orderId } });
      } else {
        console.warn('Order ID missing in URL:', url);
      }
    }
  });
});
</script>
<template>
  <div>
    <ProfileModal
      v-if="showProfileModal && passportURL"
      :passport-url="passportURL"
      @close="showProfileModal = false"
    />
    <div v-if="loading" class="fake-topbar" />
    <div v-else ref="container" />
  </div>
</template>
<style scoped>
.fake-topbar {
  width: 100%;
  height: 42px;
  background-color: #005685;
}
</style>
<style lang="scss">
#topbar .topbar-right .topbar-user {
  text-transform: capitalize;
}
/* Hotfix profile picture alignment for mobile view while waiting for Topbar Vue */
@media screen and (max-width: 700px) {
  .topbar-menu-dd .item.topbar-menu-dd-item.item-submenu .item-icon.item-image {
    margin-top: 14px;
  }
}
@media screen and (min-width: 700px) {
  .topbar-menu-dd .item.topbar-menu-dd-item.item-submenu {
    line-height: normal;
  }
}
.mp-top-frame-link {
  color: white;
  cursor: pointer;

  &:hover,
  &:active,
  &:focus {
    text-decoration: none;
    color: white;
  }
}
</style>
